// "Site Data" project-scoped overlay layers (Field Notes + Potholes).
//
// These render client-side as GeoJSON from the project geo-items API (see
// map/FieldNotesLayer.tsx + map/PotholesLayer.tsx) and only exist when a project is
// selected. They are catalog leaves (app rows, like fiberLitBuildings) whose document
// membership is guaranteed server-side (the layer-config load reconciliation always
// injects them; the Layer Library hides the group — nothing to Add/Remove): visibility
// lives in the shared `layerStore.visibleLayerIds` render set (mirrored from the
// layer-config document when `user-layer-config` is on, edited directly when off), so
// the renderers read the same set in both modes. This module
// owns the layer-id constants, the two stand-aside gates (the owner-tab axis — Field
// Notes / Potholing tabs force a single dataset — and the `site-data-layer-enabled`
// PostHog flag axis, an org-scoped kill switch independent of tab, see
// `useSiteDataLayerEnabled`), and the per-project data-availability check (a leaf with
// zero rows of its own type has nothing to show, so the picker shouldn't offer it).

import type { GeoItem } from '~/common/types';
import { filterGeoItemsByReportType } from '~/pages/projectDetails/potholing/filterGeoItemsByReportType';

export const SITE_DATA_LAYER_IDS = {
  fieldNotes: 'fieldNotes',
  potholes: 'potholes',
} as const;

export type SiteDataLayerId = (typeof SITE_DATA_LAYER_IDS)[keyof typeof SITE_DATA_LAYER_IDS];

/** Project-tab route id for the Field Notes tab (in-tab rendering owns field notes). */
export const FIELD_NOTES_TAB_ID = 'field-notes';
/** Project-tab route id for the Potholing tab (in-tab rendering owns potholes / manholes). */
export const POT_HOLING_TAB_ID = 'potholing';

/** Tab route ids where Site Data overlays stand aside (owner tabs force a single dataset). */
const SITE_DATA_OWNER_TAB_IDS: ReadonlySet<string> = new Set([FIELD_NOTES_TAB_ID, POT_HOLING_TAB_ID]);

/** The Site Data leaf ids (Field Notes + Potholes). */
const SITE_DATA_LAYER_ID_SET: ReadonlySet<string> = new Set(Object.values(SITE_DATA_LAYER_IDS));

/** Whether a layer id is one of the Site Data leaves. */
export function isSiteDataLayerId(layerId: string): boolean {
  return SITE_DATA_LAYER_ID_SET.has(layerId);
}

/** `GeoItem.type`s that render as a Field Note (mirrors `transformFieldNotes`, fieldNotes/store.ts). */
const FIELD_NOTE_GEO_ITEM_TYPES: ReadonlySet<GeoItem['type']> = new Set(['note', 'image', 'video']);

/** Whether any of the project's geo items would render as a Field Note. */
export function hasFieldNotesData(geoItems: readonly GeoItem[]): boolean {
  return geoItems.some((item) => FIELD_NOTE_GEO_ITEM_TYPES.has(item.type));
}

/** Per-project presence of each Site Data leaf's own data (see {@link isSiteDataLeafEmpty}). */
export interface SiteDataAvailability {
  fieldNotes: boolean;
  potholes: boolean;
}

/** Presence of each Site Data leaf's own data; potholes follow the org `mobileReportType`. */
export function computeSiteDataAvailability(
  geoItems: readonly GeoItem[],
  mobileReportType: string | undefined,
): SiteDataAvailability {
  return {
    fieldNotes: hasFieldNotesData(geoItems),
    potholes: filterGeoItemsByReportType(geoItems, mobileReportType).length > 0,
  };
}

/**
 * Whether Site Data picker availability is still unresolved: geo-items fetch in flight, or the
 * org detail has not finished loading (potholes availability needs `mobileReportType`).
 */
export function isSiteDataAvailabilityPending(geoItemsLoading: boolean, organizationLoaded: boolean): boolean {
  return geoItemsLoading || !organizationLoaded;
}

/**
 * Whether a Site Data leaf has zero rows of its own type in the current project — nothing to
 * show, so the picker shouldn't offer it. Non-Site-Data ids are never empty by this check.
 * When `organizationLoaded` is false, the potholes leaf is never treated as empty: a still-loading
 * org leaves `mobileReportType` undefined, which must not be interpreted as "no pothole logs".
 */
export function isSiteDataLeafEmpty(
  layerId: string,
  availability: SiteDataAvailability,
  organizationLoaded = true,
): boolean {
  if (layerId === SITE_DATA_LAYER_IDS.fieldNotes) return !availability.fieldNotes;
  if (layerId === SITE_DATA_LAYER_IDS.potholes) {
    if (!organizationLoaded) return false;
    return !availability.potholes;
  }
  return false;
}

/**
 * Whether a leaf is a Site Data layer that must be hidden from the layer PICKER: either
 * on the Field Notes / Potholing tabs (those tabs own the display and force a single
 * dataset, so the whole "Site Data" section stands aside there — both leaves hide → the
 * section renders nothing, same as outside a project), or when `siteDataLayerEnabled`
 * is false (the org-scoped kill switch, see `useSiteDataLayerEnabled`). Applies in both
 * legacy and document-managed pickers. Pure so it's unit-tested; the parallel
 * render-time gate is {@link shouldRenderSiteDataLayer}.
 */
export function isSiteDataPickerLeafHidden(
  layerId: string,
  tabId: string | undefined,
  siteDataLayerEnabled: boolean,
): boolean {
  return SITE_DATA_LAYER_ID_SET.has(layerId) && (!siteDataLayerEnabled || SITE_DATA_OWNER_TAB_IDS.has(tabId ?? ''));
}

/**
 * Whether the standalone (Surface-mounted) Site Data layer should render: `siteDataLayerEnabled`
 * is true (the org-scoped kill switch, see `useSiteDataLayerEnabled`) AND the layer is
 * visible in the shared render set (`layerStore.visibleLayerIds` — mirrored from the
 * layer-config document when `user-layer-config` is on, edited directly when off) AND we are
 * not on a Site Data owner tab ({@link FIELD_NOTES_TAB_ID} or {@link POT_HOLING_TAB_ID}).
 * Stand-aside is intentionally coarse: it blocks on **any** owner tab, not a per-layer lookup
 * (each owner tab forces its own single dataset via MapChildren). Pure so the renderers stay
 * thin and this gating is unit-tested (mirrors `shouldRegisterFiberLitBuildingsLayer`).
 */
export function shouldRenderSiteDataLayer(
  visibleLayerIds: ReadonlySet<string>,
  layerId: SiteDataLayerId,
  tabId: string | undefined,
  siteDataLayerEnabled: boolean,
): boolean {
  return siteDataLayerEnabled && visibleLayerIds.has(layerId) && !SITE_DATA_OWNER_TAB_IDS.has(tabId ?? '');
}

/**
 * Whether `tabId` is the project tab that owns in-tab rendering for this Site Data leaf
 * (Potholing ↔ potholes, Field Notes ↔ fieldNotes). Per-layer — unlike
 * {@link shouldRenderSiteDataLayer}'s coarse "any owner tab" stand-aside. Used by the
 * legend so rows appear when MapChildren draws markers on that tab.
 */
export function isSiteDataOwningTab(layerId: SiteDataLayerId, tabId: string | undefined): boolean {
  if (layerId === SITE_DATA_LAYER_IDS.potholes) return tabId === POT_HOLING_TAB_ID;
  if (layerId === SITE_DATA_LAYER_IDS.fieldNotes) return tabId === FIELD_NOTES_TAB_ID;
  return false;
}

/**
 * Whether the legend should include this Site Data leaf: either the Surface overlay is
 * rendering ({@link shouldRenderSiteDataLayer}), or we are on that leaf's owning tab
 * ({@link isSiteDataOwningTab}), where MapChildren shows the markers regardless of TOC
 * visibility / the site-data flag.
 */
export function shouldShowSiteDataInLegend(
  visibleLayerIds: ReadonlySet<string>,
  layerId: SiteDataLayerId,
  tabId: string | undefined,
  siteDataLayerEnabled: boolean,
): boolean {
  return (
    isSiteDataOwningTab(layerId, tabId) ||
    shouldRenderSiteDataLayer(visibleLayerIds, layerId, tabId, siteDataLayerEnabled)
  );
}
